import { ReactNode, useLayoutEffect, useState } from 'react';
import { Tearsheet } from '@carbon/ibm-products';
import { CHART_EXPORTING_ATTRIBUTE } from 'components/chart-export';
import {
  WellAnalysisChartPanel,
  type WellAnalysisChartPanelProps,
} from './well-analysis-chart-panel';
import styles from './well-analysis-chart-tearsheet.module.scss';

/** The shortest the chart gets, so a short window scrolls rather than squashing it. */
const MIN_CHART_HEIGHT = 300;

export interface WellAnalysisChartTearsheetProps {
  open: boolean;
  onClose: () => void;
  /** The version selectors, so versions can be changed without closing. */
  versionSelectors: ReactNode;
  chart: Omit<WellAnalysisChartPanelProps, 'height' | 'headerActions'>;
}

/**
 * The Well Analysis chart at full size, with the version selectors above it.
 * Opened from the chart's expand icon.
 *
 * The chart fills the sheet's width at a 2:1 shape, unless that would push its
 * x axis, or the stream toggles and version legend under it, past the bottom of
 * the sheet. It is then only as tall as the room left, so they stay in view.
 *
 * @param {WellAnalysisChartTearsheetProps} props - Open state, the selectors and the chart's props.
 * @return {JSX.Element} The tearsheet.
 */
export const WellAnalysisChartTearsheet = ({
  open,
  onClose,
  versionSelectors,
  chart,
}: WellAnalysisChartTearsheetProps) => {
  // Kept in state rather than refs. The Tearsheet renders its content in place
  // first and then moves it into a portal, which replaces these elements, so
  // the height has to be measured again on the new ones.
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const [selectors, setSelectors] = useState<HTMLDivElement | null>(null);
  const height = useFittedChartHeight(open, body, selectors);

  return (
    <Tearsheet
      open={open}
      onClose={onClose}
      className={styles.tearsheet}
      label="Production and Forecast"
      title={
        chart.volumeDisplayMode === 'daily'
          ? 'Daily Volumes'
          : 'Monthly Volumes'
      }
      hasCloseIcon
      closeIconDescription="Close"
    >
      {open && (
        <div ref={setBody} className={styles.body}>
          <div ref={setSelectors} className={styles.versionSelectors}>
            {versionSelectors}
          </div>
          <WellAnalysisChartPanel {...chart} height={height} />
        </div>
      )}
    </Tearsheet>
  );
};

/**
 * The chart's canvas height: half the body's width, or the room between the top
 * of the canvas and the bottom of the body, less what is kept in view under the
 * chart, when that is less.
 *
 * It is measured again when the body changes size, when the selectors above the
 * chart do, since they wrap onto more rows as the sheet narrows, when what is
 * kept in view under the chart does, since the version legend grows as versions
 * are selected, and when an image export ends.
 *
 * @param {boolean} open - Whether the tearsheet is open. Nothing is measured while it is closed.
 * @param {HTMLElement | null} body - The scrolling body holding the chart.
 * @param {HTMLElement | null} selectors - The version selectors above the chart.
 * @return {number | undefined} The height, or undefined before the first measurement.
 */
const useFittedChartHeight = (
  open: boolean,
  body: HTMLElement | null,
  selectors: HTMLElement | null
) => {
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!open || !body) return;

    const fit = () => {
      // While an image export has the chart out of the page, it can't be
      // measured. The attribute is looked for anywhere in the body, so this
      // does not depend on which element the export marks.
      if (body.querySelector(`[${CHART_EXPORTING_ATTRIBUTE}]`)) return;
      const canvas = body.querySelector<HTMLElement>('[data-chart-canvas]');
      if (!canvas) return;

      const bodyStyle = getComputedStyle(body);
      const padding = (value: string) => parseFloat(value) || 0;
      const width =
        body.clientWidth -
        padding(bodyStyle.paddingLeft) -
        padding(bodyStyle.paddingRight);

      // Measured from the body's top edge, counting what is scrolled out of view.
      const canvasRect = canvas.getBoundingClientRect();
      const canvasTop =
        canvasRect.top - body.getBoundingClientRect().top + body.scrollTop;

      // The stream toggles and version legend under the chart stay in view
      // too. The space they take below the canvas does not depend on the
      // canvas's height.
      const keptBottoms = Array.from(
        body.querySelectorAll('[data-keep-in-view]'),
        (el) => el.getBoundingClientRect().bottom
      );
      const belowHeight = keptBottoms.length
        ? Math.max(...keptBottoms) - canvasRect.bottom
        : 0;

      const room =
        body.clientHeight -
        padding(bodyStyle.paddingBottom) -
        canvasTop -
        belowHeight;

      setHeight(
        Math.max(MIN_CHART_HEIGHT, Math.floor(Math.min(width / 2, room)))
      );
    };

    fit();

    const observer = new ResizeObserver(fit);
    observer.observe(body);
    if (selectors) observer.observe(selectors);
    body
      .querySelectorAll('[data-keep-in-view]')
      .forEach((el) => observer.observe(el));

    // A size change during an image export is skipped above, so measure again
    // when the export ends.
    const exportObserver = new MutationObserver(fit);
    exportObserver.observe(body, {
      subtree: true,
      attributeFilter: [CHART_EXPORTING_ATTRIBUTE],
    });

    return () => {
      observer.disconnect();
      exportObserver.disconnect();
    };
  }, [open, body, selectors]);

  return height;
};
